import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import InputMaskForm from 'components/InputMask';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { createFeedback } from 'services/api.service';
import { onlyNumbers } from 'utils/number';

const TYPE_OPTIONS = [
  { label: 'Sugestão', value: 'SUGGESTION' },
  { label: 'Problema', value: 'PROBLEM' },
  { label: 'Melhoria', value: 'IMPROVEMENT' },
];

type FeedbackForm = { type: string; message: string; phone?: string };

const FeedbackModal: FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, reset } = useForm<FeedbackForm>({
    defaultValues: { type: '', message: '', phone: '' },
  });

  const close = () => {
    reset();
    onClose();
  };

  const submit = (data: FeedbackForm) => {
    setLoading(true);
    createFeedback({
      type: data.type,
      message: data.message,
      phone: data.phone ? onlyNumbers(data.phone) : undefined,
    })
      .then(() => {
        toast({
          title: 'Obrigado pelo seu feedback!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        close();
      })
      .catch((err) => {
        setLoading(false);
        toast({
          title: 'Erro',
          description: err?.response?.data?.message || 'Não foi possível enviar seu feedback',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Enviar feedback</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Controller
              control={control}
              name="type"
              rules={{ required: 'Selecione o tipo' }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormControl isInvalid={!!error}>
                  <FormLabel fontSize="sm">Tipo</FormLabel>
                  <SimpleGrid columns={3} spacing={3}>
                    {TYPE_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        size="sm"
                        variant={value === opt.value ? 'solid' : 'outline'}
                        colorScheme={value === opt.value ? 'brand' : 'gray'}
                        onClick={() => onChange(opt.value)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </SimpleGrid>
                  <FormErrorMessage>{error?.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="message"
              rules={{ required: 'Escreva sua mensagem' }}
              render={({ field, fieldState: { error } }) => (
                <FormControl isInvalid={!!error}>
                  <FormLabel fontSize="sm">Mensagem</FormLabel>
                  <Textarea
                    {...field}
                    placeholder="Conte pra gente o que você achou..."
                    rows={5}
                    size="sm"
                  />
                  <FormErrorMessage>{error?.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <InputMaskForm
              control={control}
              label="WhatsApp (opcional)"
              name="phone"
              type="tel"
              mask="(99) 99999-9999"
              placeholder="(11) 99999-9999"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={close} isDisabled={loading}>
              Cancelar
            </Button>
            <Button colorScheme="brand" isLoading={loading} onClick={handleSubmit(submit)}>
              Enviar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
